import { DateStringValue } from '@unistocks/components/calendar/types';
import { createDate } from '@unistocks/shared/utils/util';
import { isCanonicalDateString } from '../date-string-fast-path';

/**
 * Whether `date` falls within the inclusive `[start, end]` range, ported 1:1 from
 * Mantine's `is-in-range`. The pair is sorted first (so callers may pass the
 * endpoints in either order), then the bounds are widened by 1ms to the day's
 * edges so the comparison is inclusive of both endpoints. Pure Luxon — no DOM.
 *
 * The Mantine spelling is the most expensive per-cell call in a range picker:
 * `[...range].sort()` allocates an array and builds 2 `DateTime` instances inside
 * the comparator, then each bound costs an instance plus `startOf`/`endOf` and
 * `minus`/`plus` clones — ~11 instances per call, and `getControlProps` calls
 * it for every cell (up to 3× per cell across the range/first/last checks).
 *
 * The widened bounds exist purely to make the endpoint comparison inclusive at
 * DAY granularity, so for canonical zero-padded `yyyy-MM-dd` strings the whole
 * thing reduces to `min <= date <= max` on the date prefix — exact, and with no
 * sort, no clones and no allocations. Any non-canonical endpoint falls back to
 * the original Luxon path.
 */
export function isInRange(
  date: DateStringValue,
  range: [DateStringValue, DateStringValue]
): boolean {
  const [start, end] = range;

  if (isCanonicalDateString(date) && isCanonicalDateString(start) && isCanonicalDateString(end)) {
    const value = date.slice(0, 10);
    const from = start.slice(0, 10);
    const to = end.slice(0, 10);
    return from <= to ? value >= from && value <= to : value >= to && value <= from;
  }

  const _range = [...range].sort((a, b) => (createDate(a) > createDate(b) ? 1 : -1));
  return (
    createDate(_range[0]).startOf('day').minus({ milliseconds: 1 }) < createDate(date) &&
    createDate(_range[1]).endOf('day').plus({ milliseconds: 1 }) > createDate(date)
  );
}
